
import { ReactNode, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { UserRole } from '@/types/user';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { isNativeApp } from '@/utils/platformUtils';
import { isResetPasswordPath } from '@/utils/languageUtils';

interface RoleBasedRouteProps {
  children: ReactNode;
  requiredRoles?: UserRole[];
  fallbackPath?: string;
}

// Track access grants to prevent duplicate logging
const accessGrantTracker = new Map<string, number>();
const ACCESS_GRANT_CACHE_TTL = 60000; // 1 minute

const RoleBasedRoute = ({ 
  children, 
  requiredRoles = ['user'],
  fallbackPath 
}: RoleBasedRouteProps) => {
  const { isAuthenticated, loading, hasRole, userRole, isRecoverySession } = useAuth();
  const { t } = useLanguage();
  const navigate = useLocalizedNavigate();
  
  // Track navigation state to prevent duplicate navigations
  const navigationRef = useRef<{ route: string; timestamp: number } | null>(null);
  
  // Generate access key for caching
  const accessKey = `${window.location.pathname}-${userRole}-${requiredRoles.join(',')}`;

  // Handle navigation in useEffect to avoid "Cannot update component while rendering" warning
  useEffect(() => {
    // Don't navigate while loading
    if (loading) return;

    const currentRoute = window.location.pathname;
    const now = Date.now();

    // Prevent duplicate navigations within a short time window
    const isDuplicateNavigation = navigationRef.current &&
      navigationRef.current.route === currentRoute &&
      (now - navigationRef.current.timestamp) < 1000; // 1 second window

    if (isDuplicateNavigation) return;

    // If in recovery session and not on reset-password page, redirect to sign-in
    if (isRecoverySession && !isResetPasswordPath(currentRoute)) {
      console.log('RoleBasedRoute: Recovery session detected - blocking access to protected route:', currentRoute);
      navigationRef.current = { route: '/sign-in', timestamp: now };
      navigate('/sign-in', { replace: true });
      return;
    }

    // If not authenticated, redirect to sign-in
    if (!isAuthenticated && !isRecoverySession) {
      const signInPath = '/sign-in';
      navigationRef.current = { route: signInPath, timestamp: now };
      navigate(signInPath, { replace: true });
      return;
    }

    // If user role is loaded and user doesn't have required role, redirect to error
    if (userRole && !hasRole(requiredRoles) && !isRecoverySession) {
      console.log('RoleBasedRoute: Access denied - User role not in required roles:', {
        userRole,
        requiredRoles,
        route: currentRoute
      });
      
      navigationRef.current = { route: '/error', timestamp: now };
      navigate('/error', { 
        replace: true,
        state: { 
          title: t('error.profileInvalidTitle'), 
          message: t('error.profileInvalidMessage') 
        }
      });
    }
  }, [isAuthenticated, loading, userRole, hasRole, requiredRoles, navigate, t, isRecoverySession]);

  // Unified loading state: show nothing (let Suspense DashboardSkeleton handle it)
  // while auth is loading, redirecting, or checking permissions
  if (loading || (!isAuthenticated && !isRecoverySession) || 
      (isRecoverySession && !isResetPasswordPath(window.location.pathname)) ||
      (!userRole && !isRecoverySession) || 
      (!hasRole(requiredRoles) && !isRecoverySession)) {
    return null;
  }

  // Check if we should log access grant (prevent spam) - only for non-recovery sessions
  if (!isRecoverySession) {
    const lastAccessGrant = accessGrantTracker.get(accessKey);
    const now = Date.now();
    const shouldLogAccess = !lastAccessGrant || (now - lastAccessGrant) > ACCESS_GRANT_CACHE_TTL;

    if (shouldLogAccess) {
      console.log('RoleBasedRoute: Access granted for role:', userRole, {
        route: window.location.pathname,
        requiredRoles,
        grantedAt: new Date().toISOString()
      });
      accessGrantTracker.set(accessKey, now);
    }
  }
  
  return <>{children}</>;
};

export default RoleBasedRoute;
